import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import Loader from "components/Atoms/SharedComponents/Loader/Loader";
import setAuthToken from "services/modules/utils/handel_api";
import CartLogsTemplate from "templates/cart-logs";
import {
  getCartLogsRequest,
  getCartLogDetailsRequest,
} from "services/modules/cart-logs";
import { useThemePalette } from "common/hooks/theme_palette";

function CartLogsPage() {
  const { t } = useTranslation();
  const { orange } = useThemePalette();
  const token = localStorage.getItem("token");
  setAuthToken(token);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  let [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const type = searchParams.get("type");

  const list = useSelector((state) => state.cartLogs.cartLogs);
  const count = useSelector((state) => state.cartLogs.count);
  const details = useSelector((state) => state.cartLogs.cartLogDetails);
  const load = useSelector((state) => state.cartLogs.load);

  const [dateFilterValue, setDateFilterValue] = useState(dayjs(new Date()));
  const [selectedRow, setSelectedRow] = useState({});
  const [page, setPage] = useState(1);
  const [filterObj, setFilterObj] = useState(null);

  const handlePageChange = (page) => {
    setPage(page);
    setFilterObj((prev) => {
      return { ...prev, page_number: page };
    });
  };

  const getDetails = async () => {
    dispatch(
      getCartLogDetailsRequest({
        cart_id: selectedRow?.rowData?.id ? selectedRow?.rowData?.id : id,
      })
    );
  };

  const getList = async (filterObj) => {
    const requestData = {
      params: {
        page_size: 10,
        page_number: 1,
        keyword: "",
        sort_by: "id",
        ...filterObj,
      },
    };
    dispatch(getCartLogsRequest(requestData));
  };

  // mappedData use for create custom cell to concat multi values or navigate button
  const mappedData = list?.map((data) => {
    const currencyName = data?.currency?.name ?? data?.currency_name ?? data?.currency ?? "";
    return {
      ...data,
      amount: <>{data?.amount} {currencyName}</>,
      amount_with_vat: <>{data?.amount_with_vat} {currencyName}</>,
      delivery_fees: <>{data?.delivery_fees} {currencyName}</>,
      weight: <>{data?.weight ?? "-"}</>,
      state: <>{data?.state ?? "-"}</>,
      date: <>{dayjs(data?.created_at || data?.date).format("DD-MM-YYYY, hh a")}</>,
    };
  });

  const rowData = (data) => {
    setSelectedRow(data);
  };

  const breadcrumbs = [
    {
      active: true,
      path: "/",
      title: t("common.home"),
    },
    {
      active: !type ? false : true,
      path: "/cart-logs",
      title: t("cart_logs.title"),
    },
    type === "details" && {
      active: false,
      path: "",
      title: t("cart_logs.cart_log_details"),
    },
  ];

  const filterList = [
    {
      type: "search",
      placeholder: t("cart_logs.search_by_cart_id"),
      onChange: (value) =>
        setFilterObj((s) => {
          return { ...s, keyword: value?.trim(), page_number: 1 };
        }),
    },
    {
      type: "date",
      placeholder: "",
      onChange: (value) => {
        //if no value
        if (value != "01-01-1970") {
          setFilterObj((s) => {
            return { ...s, date: value, page_number: 1 };
          });
        } else {
          setFilterObj((s) => {
            return { ...s, date: "" };
          });
        }
      },
      sx: {
        backgroundColor: "white",
        ".MuiSvgIcon-root": { color: orange },
      },
    },
  ];

  //list of actions to be displayed when clicked in the 3 dots
  const actionsList = ({ index, data }) => {
    return [
      {
        condition: () => {
          return true;
        },
        click: () => {
          navigate(`?type=details&id=${data?.id}`);
        },
        text: t("common.view"),
      },
    ];
  };

  //if you need more buttons in the header beside the add button
  const headerChildren = <></>;

  //if more modals needed, pass them to the modalComponents as below
  const modalComponents = [];

  //listing all cart logs
  useEffect(() => {
    getList(filterObj || {});
  }, [filterObj]);

  //details of a single cart log
  useEffect(() => {
    if (id && pathname?.includes("cart-logs")) getDetails();
  }, [id, pathname]);

  return (
    <>
      <Loader open={load?.loading && load?.type == "list"} />
      <CartLogsTemplate
        filterObj={filterObj}
        setFilterObj={setFilterObj}
        data={mappedData}
        count={count}
        details={details}
        paginationPage={page}
        handlePageChange={handlePageChange}
        rowData={rowData}
        breadcrumbs={breadcrumbs}
        filterList={filterList}
        actionsList={actionsList}
        headerChildren={headerChildren}
        modalComponents={modalComponents}
        selectedRow={selectedRow}
      />
    </>
  );
}

export default CartLogsPage;